import { useState, useMemo, useEffect } from 'react';
import Navbar from './layout/Navbar';
import Hero from './sections/Hero';
import About from './sections/About';
import Experience from './sections/Experience';
import Education from './sections/Education';
import Skills from './sections/Skills';
import Projects from './sections/Projects';
import Footer from './layout/Footer';
import GalleryOverlay from './ui/GalleryOverlay';
import ScrollToTop from './ui/ScrollToTop';

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentGalleryProject, setCurrentGalleryProject] = useState('project3');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to Top Logic
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Gallery Logic
  const galleryImages = {
    project2: ["/project2_1.png", "/project2_2.png", "/project2_3.png", "/project2_4.png", "/project2_5.png"],
    project3: ["/project3_1.png", "/project3_2.png", "/project3_3.png", "/project3_4.png", "/project3_5.png", "/project3_6.png", "/project3_7.png", "/project3_8.png", "/project3_9.png"],
    project4: ["/project4_1.png", "/project4_2.png", "/project4_3.png"],
  };

  const openGallery = (projectId) => {
    setCurrentGalleryProject(projectId);
    setCurrentImageIndex(0);
    setGalleryOpen(true);
  };

  const navigateGallery = (direction) => {
    const images = galleryImages[currentGalleryProject] || [];
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    } else {
      setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  };

  // Shared Animation Variants
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 24 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  // Stable floating shapes
  const floatingShapes = useMemo(() => 
    Array.from({ length: 8 }, (_, i) => ({
      width: Math.random() * 50 + 30,
      height: Math.random() * 50 + 30,
      top: Math.random() * 100,
      left: Math.random() * 100,
      duration: Math.random() * 8 + 10,
      delay: i * 0.6,
    })), []
  );

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300 relative selection:bg-blue-500/30">
      <Navbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      
      <main>
        <Hero floatingShapes={floatingShapes} />
        <About staggerItem={staggerItem} />
        <Experience staggerItem={staggerItem} />
        <Education staggerItem={staggerItem} />
        <Skills staggerContainer={staggerContainer} staggerItem={staggerItem} />
        <Projects openGallery={openGallery} staggerContainer={staggerContainer} staggerItem={staggerItem} />
      </main>

      <Footer staggerItem={staggerItem} />

      <GalleryOverlay 
        galleryOpen={galleryOpen}
        closeGallery={() => setGalleryOpen(false)}
        currentImageIndex={currentImageIndex}
        images={galleryImages[currentGalleryProject] || []}
        navigateGallery={navigateGallery}
      />

      <ScrollToTop showScrollTop={showScrollTop} scrollToTop={scrollToTop} />
    </div>
  );
};

export default Portfolio;